import React, { useMemo, useEffect, useCallback } from "react";
import { usePrevious } from "../../hooks";
import {
  Card,
  CardContent,
  Grid,
  Typography,
  Button,
  FormHelperText,
} from "@mui/material";
import { Delete as DeleteIcon, Send as SendIcon } from "@mui/icons-material";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Form } from "react-final-form";

import { Select, TextField } from "../common/Fields";
import { Content } from "../layout";
import {
  getRepeater,
  submitRepeater,
  updateRepeater,
  fetchApplications,
  deleteRepeater,
} from "../../actions";
import { getHexStr } from "../../actions/utils";

const RepeaterForm = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { devEUI } = useParams();

  const devAddrValue = useMemo(() => getHexStr(8), []);
  const nwkSEncKeyValue = useMemo(() => getHexStr(32), []);
  const appSKeyValue = useMemo(() => getHexStr(32), []);

  const { repeater } = useSelector((state) => state.repeater);
  const applications = useSelector(
    (state) => state.application.applications.rows
  );
  const { user } = useSelector((state) => state.auth);
  const prevOrganization = usePrevious(user?.Organization?.id);

  useEffect(() => {
    dispatch(fetchApplications({ page: null, limit: null, all: false }));
    if (devEUI) {
      dispatch(getRepeater(devEUI));
    }
  }, [devEUI, user, dispatch]);

  useEffect(() => {
    if (user && prevOrganization) {
      if (user.Organization.id !== prevOrganization) {
        navigate("/repeaters");
      }
    }
  }, [user, prevOrganization, navigate]);

  const onSubmit = useCallback(
    async (repeater) => {
      const upsertRepeater = new Promise((resolve, reject) => {
        if (devEUI) {
          dispatch(updateRepeater({ repeater, devEUI }))
            .then(() => resolve())
            .catch((error) => reject(error));
        } else {
          dispatch(submitRepeater({ repeater }))
            .then(() => resolve())
            .catch((error) => reject(error));
        }
      });

      try {
        await upsertRepeater;
        navigate("/repeaters");
      } catch (error) {
        let errors;
        if (error.response.data?.errors) {
          const arrayErrors = error.response.data.errors;
          arrayErrors.forEach((error) => {
            errors = { ...errors, [error.param]: error.msg };
          });
        }
        return errors;
      }
    },
    [devEUI, dispatch, navigate]
  );

  const onDelete = useCallback(
    (e) => {
      e.preventDefault();
      console.log("devEUI", devEUI);
      dispatch(deleteRepeater({ devEUI }, navigate));
    },
    [devEUI, dispatch, navigate]
  );

  return (
    <Form
      onSubmit={onSubmit}
      initialValues={{
        name: repeater?.name,
        description: repeater?.description,
        ApplicationId: repeater?.Application.id,
        devAddr: repeater?.devAddr ?? devAddrValue,
        nwkSEncKey: repeater?.nwkSEncKey ?? nwkSEncKeyValue,
        appSKey: repeater?.appSKey ?? appSKeyValue,
      }}
      validate={({
        name,
        description,
        ApplicationId,
        devAddr,
        nwkSEncKey,
        appSKey,
      }) => {
        const errors = {};
        if (!name) {
          errors.name = "필수";
        }
        if (!description) {
          errors.description = "필수";
        }
        if (!ApplicationId) {
          errors.ApplicationId = "필수";
        }
        if (!devAddr) {
          errors.devAddr = "필수";
        }
        if (!nwkSEncKey) {
          errors.nwkSEncKey = "필수";
        }
        if (!appSKey) {
          errors.appSKey = "필수";
        }
        return errors;
      }}
      render={({
        submitError,
        handleSubmit,
        form,
        submitting,
        pristine,
        values,
      }) => (
        <Content>
          <form onSubmit={handleSubmit}>
            <Card>
              <CardContent>
                <Typography variant="h5" component="div">
                  작업장(리피터)
                </Typography>
                <Grid
                  container
                  alignItems="flex-start"
                  spacing="20"
                  sx={{ mt: 2 }}
                >
                  <Grid item xs={12}>
                    <Select
                      name="ApplicationId"
                      label="협력사"
                      variant="standard"
                      data={applications}
                      dataKey="id"
                      dataValue="name"
                      disabled={devEUI ? true : false}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      name="name"
                      type="text"
                      label="구분값"
                      helperText="영문자, 숫자, '-'만 가능합니다."
                      inputProps={{
                        pattern: "[\\w-]+",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      name="description"
                      type="text"
                      label="이름"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      name="devAddr"
                      type="text"
                      label="Device Address"
                      helperText="HEX 문자열만 입력할 수 있습니다."
                      inputProps={{
                        pattern: "[0-9a-fA-F]{8}",
                        maxLength: "8",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      name="nwkSEncKey"
                      type="text"
                      label="Network Session Key"
                      helperText="HEX 문자열만 입력할 수 있습니다."
                      inputProps={{
                        pattern: "[0-9a-fA-F]{32}",
                        maxLength: "32",
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      name="appSKey"
                      type="text"
                      label="Application Session Key"
                      helperText="HEX 문자열만 입력할 수 있습니다."
                      inputProps={{
                        pattern: "[0-9a-fA-F]{32}",
                        maxLength: "32",
                      }}
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            {submitError && <FormHelperText>{submitError}</FormHelperText>}
            <Grid container sx={{ mt: 2 }}>
              <Grid item xs={6}>
                {devEUI && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={onDelete}
                  >
                    삭제
                  </Button>
                )}
              </Grid>
              <Grid item xs={6} container justifyContent="flex-end">
                <Button
                  variant="contained"
                  endIcon={<SendIcon />}
                  type="submit"
                >
                  저장
                </Button>
              </Grid>
            </Grid>
          </form>
        </Content>
      )}
    />
  );
};

export default RepeaterForm;
